// Layout blocks: container, box, row and column.
import { registerBlock } from '../core/registry.js';
import { classList, replaceClasses, findClass } from '../core/dom.js';
import { isColumn, newColumnClasses } from '../core/grid.js';

const CONTAINER_CLASS = /^container(?:-(?:sm|md|lg|xl|xxl|fluid))?$/;
const GUTTER_CLASS = /^g[xy]?-(?:0|1|2|3|4|5)$/;
const JUSTIFY_CLASS = /^justify-content-(?:start|center|end|between|around|evenly)$/;
const ALIGN_ITEMS_CLASS = /^align-items-(?:start|center|end|baseline|stretch)$/;
const ALIGN_SELF_CLASS = /^align-self-(?:start|center|end|baseline|stretch)$/;
const BOX_TAGS = new Set(['div', 'section', 'article', 'header', 'footer', 'aside', 'main', 'nav']);

export const TEXTUAL_TAGS = new Set(['p', 'ul', 'ol', 'dl', 'blockquote', 'pre', 'table', 'address', 'h2', 'h3', 'h4', 'h5', 'h6']);
export const INLINE_TAGS = new Set([
    'a', 'abbr', 'b', 'bdi', 'bdo', 'br', 'cite', 'code', 'data', 'dfn', 'em', 'i', 'kbd', 'mark', 'q', 's', 'samp',
    'small', 'span', 'strong', 'sub', 'sup', 'time', 'u', 'var', 'wbr', 'del', 'ins', 'label',
]);

registerBlock({
    name: 'container',
    label: 'block.container',
    icon: 'rectangle',
    category: 'layout',
    kind: 'flow',
    priority: 90,
    match: el => classList(el).some(name => CONTAINER_CLASS.test(name)),
    create: ({ h }) => h('div', { class: 'container' }),
    accepts: name => name !== 'container',
    presets: [
        { id: 'container-fluid', label: 'block.containerFluid', icon: 'arrows-out-line-horizontal', html: '<div class="container-fluid"></div>' },
    ],
    fields: [
        {
            type: 'segmented',
            label: 'field.width',
            options: [
                { value: 'container', label: 'option.boxed' },
                { value: 'container-fluid', label: 'option.fullWidth' },
            ],
            get: el => findClass(el, CONTAINER_CLASS) || 'container',
            set: (el, value) => replaceClasses(el, CONTAINER_CLASS, value),
            pattern: CONTAINER_CLASS,
        },
    ],
});

registerBlock({
    name: 'box',
    label: 'block.box',
    icon: 'selection-all',
    category: 'layout',
    kind: 'flow',
    priority: 5,
    match: el => BOX_TAGS.has(el.localName) && !el.classList.contains('pflow-html'),
    create: ({ h }) => h('div', { class: 'p-4 rounded-3 bg-light' }),
    fields: [
        {
            type: 'select',
            label: 'field.tag',
            options: ['div', 'section', 'article', 'header', 'footer', 'aside'].map(value => ({ value, label: `<${value}>` })),
            get: el => el.localName,
            set: (el, value, ctx) => ctx.retag(el, value),
        },
    ],
});

registerBlock({
    name: 'row',
    label: 'block.row',
    icon: 'columns',
    category: 'layout',
    kind: 'row',
    priority: 80,
    palette: false, // offered through the column presets below
    match: el => el.classList.contains('row'),
    create: ({ h }) => h('div', { class: 'row' }, h('div', { class: 'col-12 col-md-6' }), h('div', { class: 'col-12 col-md-6' })),
    fields: [
        {
            type: 'select',
            label: 'field.gutters',
            options: [{ value: '', label: 'option.default' }, ...['0', '1', '2', '3', '4', '5'].map(value => ({ value: `g-${value}`, label: value }))],
            get: el => findClass(el, GUTTER_CLASS),
            set: (el, value) => replaceClasses(el, GUTTER_CLASS, value),
            pattern: GUTTER_CLASS,
        },
        {
            type: 'segmented',
            label: 'field.justify',
            icons: true,
            options: [
                { value: '', label: 'option.default', icon: 'x' },
                { value: 'justify-content-start', label: 'option.start', icon: 'text-align-left' },
                { value: 'justify-content-center', label: 'option.center', icon: 'text-align-center' },
                { value: 'justify-content-end', label: 'option.end', icon: 'text-align-right' },
                { value: 'justify-content-between', label: 'option.between', icon: 'text-align-justify' },
            ],
            get: el => findClass(el, JUSTIFY_CLASS),
            set: (el, value) => replaceClasses(el, JUSTIFY_CLASS, value),
            pattern: JUSTIFY_CLASS,
        },
        {
            type: 'segmented',
            label: 'field.alignItems',
            icons: true,
            options: [
                { value: '', label: 'option.default', icon: 'x' },
                { value: 'align-items-start', label: 'option.top', icon: 'arrow-up' },
                { value: 'align-items-center', label: 'option.middle', icon: 'arrows-in-line-horizontal' },
                { value: 'align-items-end', label: 'option.bottom', icon: 'arrow-down' },
            ],
            get: el => findClass(el, ALIGN_ITEMS_CLASS),
            set: (el, value) => replaceClasses(el, ALIGN_ITEMS_CLASS, value),
            pattern: ALIGN_ITEMS_CLASS,
        },
        { type: 'row-actions' },
    ],
    // Column layouts of the palette: each one creates a row with its columns.
    presets: [
        { id: 'cols-1', label: 'preset.cols1', icon: 'square', sizes: [12] },
        { id: 'cols-2', label: 'preset.cols2', icon: 'columns', sizes: [6, 6] },
        { id: 'cols-3', label: 'preset.cols3', icon: 'columns', sizes: [4, 4, 4] },
        { id: 'cols-4', label: 'preset.cols4', icon: 'grid-four', sizes: [3, 3, 3, 3] },
        { id: 'cols-8-4', label: 'preset.cols84', icon: 'sidebar-simple', sizes: [8, 4] },
        { id: 'cols-4-8', label: 'preset.cols48', icon: 'sidebar-simple', sizes: [4, 8] },
        { id: 'cols-3-6-3', label: 'preset.cols363', icon: 'columns', sizes: [3, 6, 3] },
        { id: 'cols-6', label: 'preset.cols6', icon: 'grid-four', sizes: [2, 2, 2, 2, 2, 2], breakpoint: 'lg' },
    ].map(preset => ({
        ...preset,
        category: 'layout',
        html: '<div class="row">' + preset.sizes.map(size => {
            const classes = preset.breakpoint === 'lg' && size === 2 ? 'col-6 col-md-4 col-lg-2' : newColumnClasses(size);
            return `<div class="${classes}"></div>`;
        }).join('') + '</div>',
    })),
});

registerBlock({
    name: 'col',
    label: 'block.col',
    icon: 'square-split-horizontal',
    category: 'layout',
    kind: 'col',
    priority: 0,
    palette: true,
    match: (el, ctx) => ctx && ctx.parentKind === 'row' && isColumn(el),
    create: ({ h }) => h('div', { class: 'col-12 col-md' }),
    fields: [
        { type: 'columns' },
        {
            type: 'segmented',
            label: 'field.alignSelf',
            icons: true,
            options: [
                { value: '', label: 'option.default', icon: 'x' },
                { value: 'align-self-start', label: 'option.top', icon: 'arrow-up' },
                { value: 'align-self-center', label: 'option.middle', icon: 'arrows-in-line-horizontal' },
                { value: 'align-self-end', label: 'option.bottom', icon: 'arrow-down' },
            ],
            get: el => findClass(el, ALIGN_SELF_CLASS),
            set: (el, value) => replaceClasses(el, ALIGN_SELF_CLASS, value),
            pattern: ALIGN_SELF_CLASS,
        },
    ],
});

export const LAYOUT_PATTERNS = [CONTAINER_CLASS, /^row$/, GUTTER_CLASS, JUSTIFY_CLASS, ALIGN_ITEMS_CLASS, ALIGN_SELF_CLASS];
